import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import useStoreProductCategories from '@/common/hooks/useStoreProductCategories/useStoreProductCategories'
import useStoreProductCollections from '@/common/hooks/useStoreProductCollections/useStoreProductCollections'
import {
  productTaxonomyDisplayOptions,
  productTaxonomyTypeOptions,
  widgetDefault,
} from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/TryOnWidgetConfigurationPage/constants'
import configurationServices from '@/configuration/services/services'

import { getEditPreferencesPayload } from './helpers'

const useTryOnWidgetConfiguration = ({ storeId, setToastInfo, storeProvider }) => {
  const { t } = useTranslation()
  const { data, loading } = useFetch(() => configurationServices.getTryOnWidgetPreferences({ storeId }), {
    doInitialRequest: true,
  })
  const { data: previewProduct } = useFetch(() => configurationServices.getTryOnWidgetPreview({ storeId }), {
    doInitialRequest: true,
  })
  const { productCategories, fetchingProductCategories } = useStoreProductCategories({
    storeId,
    doInitialRequest: true,
  })
  const { productCollections, fetchingProductCollections } = useStoreProductCollections({
    storeId,
    doInitialRequest: true,
  })
  const { fetchData: editTryOnWidgetPreferences, loading: editing } = useFetch(
    configurationServices.editTryOnWidgetPreferences,
    { doInitialRequest: false }
  )

  const [backgroundColor, setBackgroundColor] = useState(widgetDefault.background)
  const [textColor, setTextColor] = useState(widgetDefault.text)
  const [buttonColor, setButtonColor] = useState(widgetDefault.button)
  const [buttonHoverColor, setButtonHoverColor] = useState(widgetDefault.buttonHover)
  const [productTaxonomyType, setProductTaxonomyType] = useState(productTaxonomyTypeOptions.category)
  const [productTaxonomyDisplay, setProductTaxonomyDisplay] = useState(productTaxonomyDisplayOptions.all)
  const [productTaxonomyIds, setProductTaxonomyIds] = useState([])
  const [hasChanges, setHasChanges] = useState(false)

  const setInitValues = useCallback(() => {
    if (data) {
      setBackgroundColor(data?.background_color || widgetDefault.background)
      setTextColor(data?.text_color || widgetDefault.text)
      setButtonColor(data?.button_color || widgetDefault.button)
      setButtonHoverColor(data?.button_hover_color || widgetDefault.buttonHover)
      setProductTaxonomyType(data.product_taxonomy_type)
      setProductTaxonomyDisplay(data?.product_taxonomy_display || productTaxonomyDisplayOptions.all)
      setProductTaxonomyIds(data?.product_taxonomy_ids || [])
      setHasChanges(false)
    }
  }, [data])

  useEffect(() => {
    setInitValues()
  }, [setInitValues])

  const revertChanges = () => {
    setInitValues()
  }

  const scriptInfo = `<div id="reveni-try-on-widget"></div>
  <script defer="defer" type="text/javascript" src="${import.meta.env.VITE_REVENI_TRY_ON_WIDGET}?storeId=${storeId}&platform=${storeProvider}&language={{ request.locale.iso_code }}"></script>`

  const onSave = async () => {
    try {
      const preferencesPayload = getEditPreferencesPayload({
        backgroundColor,
        textColor,
        buttonColor,
        buttonHoverColor,
        productTaxonomyType,
        productTaxonomyDisplay,
        productTaxonomyIds,
      })

      await editTryOnWidgetPreferences({ storeId, preferences: preferencesPayload })
      setHasChanges(false)
      setToastInfo({ show: true, type: 'success', message: t('Cambios guardados') })
    } catch (error) {
      setToastInfo({ show: true, type: 'error', message: error.errorMessage })
    }
  }

  return {
    fetchingTryOnWidgetPreferences: loading || fetchingProductCategories || fetchingProductCollections,
    hasChanges,
    backgroundColor,
    setBackgroundColor: param => {
      setHasChanges(true)
      setBackgroundColor(param)
    },
    textColor,
    setTextColor: param => {
      setHasChanges(true)
      setTextColor(param)
    },
    buttonColor,
    setButtonColor: param => {
      setHasChanges(true)
      setButtonColor(param)
    },
    buttonHoverColor,
    setButtonHoverColor: param => {
      setHasChanges(true)
      setButtonHoverColor(param)
    },
    productTaxonomyType,
    setProductTaxonomyType: param => {
      setHasChanges(true)
      setProductTaxonomyType(param)
      setProductTaxonomyIds([])
    },
    productTaxonomyDisplay,
    setProductTaxonomyDisplay: param => {
      setHasChanges(true)
      setProductTaxonomyDisplay(param)
    },
    productTaxonomyIds,
    setProductTaxonomyIds: param => {
      setHasChanges(true)
      setProductTaxonomyIds(param)
    },
    productCategories,
    productCollections,
    previewProduct,
    tryonMode: data?.tryon_mode,
    revertChanges,
    scriptInfo,
    onSave,
    editing,
  }
}

export default useTryOnWidgetConfiguration
